// src/pages/Reports/AgentDebtReport/index.tsx
// dac-ta-bao-cao-cong-no-dai-ly.md §18/§19/§29 — Báo cáo công nợ đại lý theo mô hình Sổ cái:
// công nợ đầu kỳ / phát sinh trong kỳ / công nợ cuối kỳ + dashboard dòng tiền. Dữ liệu lấy từ
// ticket-service (bảng chiếu AgentDebtLedgerEntries), FE chỉ đọc.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Col, Container, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';

import BreadCrumb from 'Components/Common/BreadCrumb';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import {
  downloadAgentDebtDetailExport,
  downloadAgentDebtSummaryExport,
  getAgentDebtReportApi,
  getIssuancePlans,
  rebuildAgentDebtApi,
} from 'helpers/fakebackend_helper';
import { AgentMap, buildAgentMap, getAgentCode, getAgentName, loadAgentsWithCache } from 'helpers/agentLookup';
import { IAgent } from 'slices/agentManagement/types';
import { IssuancePlan } from 'pages/IssuancePlan/types';
import { formatCurrency, cleanParams } from 'pages/Reports/IssuanceReporting/shared/formatters';
import { useBrandingTitle } from '../../../hooks';
import { AgentDebtReportRow, AgentDebtReportSummary, ReportPagination } from '../types';
import AgentDebtDetailModal from './AgentDebtDetailModal';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_agent_debt_report');

const PAGE_SIZE = 10;

const emptySummary: AgentDebtReportSummary = {
  totalOpeningBalance: 0, totalTicketSaleValue: 0, totalTicketReturnValue: 0, totalSalesCommission: 0,
  totalPrizePayoutCommission: 0, totalAdjustmentIncrease: 0, totalAdjustmentDecrease: 0, totalPaidAmount: 0,
  totalClosingBalance: 0, totalNetMovement: 0, totalCashIn: 0, totalCashOut: 0, totalCashNet: 0,
  totalCashInCumulative: 0, totalCashOutCumulative: 0, totalCashNetCumulative: 0,
  agentCount: 0,
};

const getPayloadItems = (response: any): any[] => {
  const payload = response?.data || response;
  return payload?.items || payload?.data || payload || [];
};

// Ô thẻ số liệu tổng hợp.
const StatCard: React.FC<{ label: string; value: number; tone?: string }> = ({ label, value, tone }) => (
  <Col md={3} sm={6}>
    <Card className="card-animate mb-3">
      <CardBody>
        <p className="text-muted text-uppercase fw-medium mb-2" style={{ fontSize: 12 }}>{label}</p>
        <h4 className={`mb-0 ${tone ?? ''}`}>{formatCurrency(value)}</h4>
      </CardBody>
    </Card>
  </Col>
);

const AgentDebtReport: React.FC = () => {
  useBrandingTitle(st('bao_cao_cong_no_dai_ly_text'));

  const [plans, setPlans] = useState<IssuancePlan[]>([]);
  const [agents, setAgents] = useState<IAgent[]>([]);
  const agentMap: AgentMap = useMemo(() => buildAgentMap(agents), [agents]);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [planId, setPlanId] = useState('');
  const [agentId, setAgentId] = useState('');
  const [agentCode, setAgentCode] = useState('');

  const [loading, setLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [rows, setRows] = useState<AgentDebtReportRow[]>([]);
  const [summary, setSummary] = useState<AgentDebtReportSummary>(emptySummary);
  const [pagination, setPagination] = useState<ReportPagination>({
    currentPage: 1, pageSize: PAGE_SIZE, totalItems: 0, totalPages: 0,
  });
  const [detailTarget, setDetailTarget] = useState<AgentDebtReportRow | null>(null);

  useEffect(() => {
    getIssuancePlans({ pageIndex: 1, pageSize: 200 })
      .then((res) => setPlans(getPayloadItems(res) as IssuancePlan[]))
      .catch(() => toast.error(st('khong_the_tai_danh_sach_ke_message')));
    loadAgentsWithCache().then(setAgents);
  }, []);

  // "Mã đại lý" -> danh sách AgentId truyền lên BE (param agentIds, csv).
  const resolvedAgentIds = useMemo(() => {
    const code = agentCode.trim().toLowerCase();
    if (!code) return '';
    return agents
      .filter((a) => (a.code || '').toLowerCase().includes(code) || (a.name || '').toLowerCase().includes(code))
      .map((a) => a.id)
      .join(',');
  }, [agentCode, agents]);

  const buildFilter = useCallback(() => cleanParams({
    FromDate: fromDate || undefined,
    ToDate: toDate || undefined,
    PlanId: planId || undefined,
    AgentId: agentId || undefined,
    AgentIds: !agentId ? resolvedAgentIds || undefined : undefined,
  }), [fromDate, toDate, planId, agentId, resolvedAgentIds]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAgentDebtReportApi({
        ...buildFilter(),
        PageIndex: pagination.currentPage,
        PageSize: pagination.pageSize,
      });
      const payload = (res as any)?.data ?? res;
      const page = payload?.page ?? payload?.Page ?? {};
      setRows(page.items ?? []);
      setSummary(payload?.summary ?? payload?.Summary ?? emptySummary);
      setPagination((prev) => ({
        ...prev,
        totalItems: page.totalItems ?? 0,
        totalPages: page.totalPages ?? 0,
      }));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_bao_cao_message'));
    } finally {
      setLoading(false);
    }
  }, [buildFilter, pagination.currentPage, pagination.pageSize]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const handleSearch = () => setPagination((prev) => ({ ...prev, currentPage: 1 }));

  const handleRebuild = async () => {
    setRebuilding(true);
    try {
      await rebuildAgentDebtApi(agentId ? Number(agentId) : undefined);
      toast.success(st('da_cap_nhat_lai_so_lieu_message'));
      await loadReport();
    } catch (error: any) {
      toast.error(error?.message || st('cap_nhat_so_lieu_that_bai_message'));
    } finally {
      setRebuilding(false);
    }
  };

  const handleExport = async (format: 'Xlsx' | 'Csv') => {
    try {
      await downloadAgentDebtSummaryExport(buildFilter(), format);
    } catch (error: any) {
      toast.error(error?.message || st('xuat_bao_cao_that_bai_message'));
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('bao_cao_cong_no_dai_ly_text')} pageTitle={st('bao_cao_text')} />

          {/* Bộ lọc (§3) */}
          <Card>
            <CardHeader className="border-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
              <h5 className="card-title mb-0">{st('bao_cao_cong_no_dai_ly_text')}</h5>
              <div className="d-flex gap-2">
                <Button color="light" size="sm" disabled={rebuilding} onClick={handleRebuild}>
                  {rebuilding ? <Spinner size="sm" /> : <i className="ri-refresh-line align-bottom me-1" />}
                  {st('cap_nhat_so_lieu_button')}
                </Button>
                <Button color="success" size="sm" outline onClick={() => handleExport('Xlsx')}>
                  <i className="ri-file-excel-2-line align-bottom me-1" />{st('excel_button')}
                </Button>
                <Button color="secondary" size="sm" outline onClick={() => handleExport('Csv')}>{st('csv_button')}</Button>
              </div>
            </CardHeader>
            <CardBody>
              <Row className="g-3 mb-1 align-items-end">
                <Col md={2}>
                  <Label className="form-label">{st('tu_ngay_label')}</Label>
                  <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </Col>
                <Col md={2}>
                  <Label className="form-label">{st('den_ngay_label')}</Label>
                  <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </Col>
                <Col md={3}>
                  <Label className="form-label">{st('ke_hoach_phat_hanh_label')}</Label>
                  <Input type="select" value={planId} onChange={(e) => setPlanId(e.target.value)}>
                    <option value="">{st('tat_ca_ke_hoach_option')}</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>{plan.planCode}</option>
                    ))}
                  </Input>
                </Col>
                <Col md={3}>
                  <Label className="form-label">{st('dai_ly_label')}</Label>
                  <Input type="select" value={agentId} onChange={(e) => setAgentId(e.target.value)}>
                    <option value="">{st('tat_ca_dai_ly_option')}</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>{agent.code} - {agent.name}</option>
                    ))}
                  </Input>
                </Col>
                <Col md={2}>
                  <Label className="form-label">{st('ma_dai_ly_label')}</Label>
                  <Input value={agentCode} placeholder={st('tim_theo_ma_ten_placeholder')} onChange={(e) => setAgentCode(e.target.value)} disabled={!!agentId} />
                </Col>
                <Col md={2}>
                  <Button color="primary" className="w-100" onClick={handleSearch}>
                    <i className="ri-search-line align-bottom me-1" />{st('tim_kiem_button')}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* §18 header — chỉ tiêu công nợ tổng hợp */}
          <Row>
            <StatCard label={st('tong_cong_no_dau_ky_label')} value={summary.totalOpeningBalance} />
            <StatCard label={st('phat_sinh_rong_trong_ky_label')} value={summary.totalNetMovement} />
            <StatCard label={st('da_thanh_toan_label')} value={summary.totalPaidAmount} tone="text-success" />
            <StatCard label={st('cong_no_cuoi_ky_column')} value={summary.totalClosingBalance} tone="text-danger fw-bold" />
          </Row>

          {/* §19 dashboard dòng tiền */}
          <Row>
            <Col lg={6}>
              <Card><CardBody>
                <h6 className="text-muted mb-3">{st('dong_tien_trong_ky_title')}</h6>
                <Row className="text-center">
                  <Col><small className="text-muted d-block">{st('tien_vao_text')}</small><span className="text-success">{formatCurrency(summary.totalCashIn)}</span></Col>
                  <Col><small className="text-muted d-block">{st('tien_ra_text')}</small><span className="text-danger">{formatCurrency(summary.totalCashOut)}</span></Col>
                  <Col><small className="text-muted d-block">{st('thuan_column')}</small><span className="fw-bold">{formatCurrency(summary.totalCashNet)}</span></Col>
                </Row>
              </CardBody></Card>
            </Col>
            <Col lg={6}>
              <Card><CardBody>
                <h6 className="text-muted mb-3">{st('dong_tien_luy_ke_title')}</h6>
                <Row className="text-center">
                  <Col><small className="text-muted d-block">{st('tien_vao_text')}</small><span className="text-success">{formatCurrency(summary.totalCashInCumulative)}</span></Col>
                  <Col><small className="text-muted d-block">{st('tien_ra_text')}</small><span className="text-danger">{formatCurrency(summary.totalCashOutCumulative)}</span></Col>
                  <Col><small className="text-muted d-block">{st('thuan_column')}</small><span className="fw-bold">{formatCurrency(summary.totalCashNetCumulative)}</span></Col>
                </Row>
              </CardBody></Card>
            </Col>
          </Row>

          <Card>
            <CardBody>
              <div className="d-flex gap-3 mb-3 text-muted" style={{ fontSize: 13 }}>
                <span>{st('so_dai_ly_text')} <b>{summary.agentCount}</b></span>
              </div>

              {loading ? (
                <div className="text-center py-5"><Spinner color="primary" /></div>
              ) : (
                <>
                  <DataTableShell
                    headerRow={
                      <tr>
                        <th className="text-center" style={{ width: 46 }}>{st('stt_column')}</th>
                        <th>{st('dai_ly_label')}</th>
                        <th className="text-end">{st('dau_ky_column')}</th>
                        <th className="text-end">{st('ve_ban_label')}</th>
                        <th className="text-end">{st('thu_hoi_column')}</th>
                        <th className="text-end">{st('hh_ban_column')}</th>
                        <th className="text-end">{st('hh_thuong_column')}</th>
                        <th className="text-end">{st('dieu_chinh_column')}</th>
                        <th className="text-end">{st('da_thu_column')}</th>
                        <th className="text-end">{st('cuoi_ky_column')}</th>
                        <th style={{ width: 70 }} className="text-center">{st('chi_tiet_column')}</th>
                      </tr>
                    }
                  >
                    {rows.length === 0 ? (
                      <tr><td colSpan={11} className="text-center text-muted py-5">{st('khong_co_du_lieu_text')}</td></tr>
                    ) : (
                      rows.map((row, index) => (
                        <tr key={row.agentId}>
                          <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
                          <td>
                            <div className="fw-medium">{getAgentName(agentMap, row.agentId)}</div>
                            <small className="text-muted">{getAgentCode(agentMap, row.agentId)}</small>
                          </td>
                          <td className="text-end">{formatCurrency(row.openingBalance)}</td>
                          <td className="text-end">{formatCurrency(row.ticketSaleValue)}</td>
                          <td className="text-end">{formatCurrency(row.ticketReturnValue)}</td>
                          <td className="text-end">{formatCurrency(row.salesCommission)}</td>
                          <td className="text-end">{formatCurrency(row.prizePayoutCommission)}</td>
                          <td className="text-end">{formatCurrency(row.adjustmentIncrease - row.adjustmentDecrease)}</td>
                          <td className="text-end text-success">{formatCurrency(row.paidAmount)}</td>
                          <td className={`text-end fw-bold ${row.closingBalance > 0 ? 'text-danger' : 'text-success'}`}>
                            {formatCurrency(row.closingBalance)}
                          </td>
                          <td className="text-center">
                            <Button color="info" outline size="sm" className="btn-icon" title={st('xem_chi_tiet_title')}
                              onClick={() => setDetailTarget(row)}>
                              <i className="ri-eye-line" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </DataTableShell>

                  <AgentPagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalItems}
                    pageSize={pagination.pageSize}
                    onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
                  />
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      <AgentDebtDetailModal
        isOpen={!!detailTarget}
        agentId={detailTarget?.agentId ?? null}
        agentMap={agentMap}
        fromDate={fromDate || undefined}
        toDate={toDate || undefined}
        planId={planId ? Number(planId) : undefined}
        onExport={(format) =>
          detailTarget &&
          downloadAgentDebtDetailExport(detailTarget.agentId, cleanParams({
            fromDate: fromDate || undefined, toDate: toDate || undefined, planId: planId || undefined,
          }), format).catch((e: any) => toast.error(e?.message || st('xuat_file_that_bai_message')))
        }
        onClose={() => setDetailTarget(null)}
      />
    </React.Fragment>
  );
};

export default AgentDebtReport;
